/**
 * English mirror of ./strings.ar.js — GENERATED by scripts/i18n/build.mjs.
 *
 * Wording follows the legacy Locale/*.resx English resources wherever the
 * legacy screen had the same label. To change any of it, edit
 * scripts/i18n/strings.en.json and re-run the script.
 */

export const strings = {
  title: 'General Statistics',
  subtitle: 'General figures',
  hello: 'Hello, ',

  filter: {
    from: 'From',
    to: 'To',
    submit: 'Report',
  },

  shares: {
    success: 'Success Percentage',
    failure: 'Failure Percentage',
    incomplete: 'Uncompleted Percentage',
    incompleteNote: '(Attend at least one exam but did not complete other exams)',
    absent: 'Unattended Percentage',
    absentNote: '(Did not attend and any exam)',
  },

  counters: {
    programs: 'Programs',
    courses: 'Courses',
    categories: 'Categories',
    activePrograms: 'Active Programs',
    activeCourses: 'Active Courses',
    remainingAccounts: 'Remaining Accounts',
    consumedAccounts: 'Consumed Accounts',
    remainingDays: 'Remaining Days',
    expiresOn: 'End Date',
  },

  license: {
    title: 'License',
    hint: 'View license details',
    seats: 'Remaining Accounts',
    seatsUsed: 'Used',
    days: 'Remaining Days',
    expiresOn: 'Expires on',
  },

  series: {
    success: 'Success Count',
    failure: 'Failure Count',
    incomplete: 'Uncompleted Count',
    absent: 'Unattended Count',
    passed: 'Passed Count',
    failed: 'Failed Count',
  },

  charts: {
    programs: 'Learning Programs Statistics (active)',
    programsNote: 'The graph shows the number of users and their status for each of the training program',
    programsLargest: 'Largest programmes:',
    programsRest: 'the rest are in the table:',
    exams: 'Course exams completed per day (passed / not passed)',
    assessments: 'Assessment results',
    period: 'Period',

    allTime: 'Not affected by the selected period',
    noAssessments: 'No assessments yet',
  },

  loadFailed: 'The statistics could not be loaded',
  notPermitted: 'You do not have permission to view the statistics',
  notPermittedHint: 'This screen is available to tenant administrators.',

  table: {
    title: 'Program General Details (active)',
    subtitle: 'Courses',
    program: 'Programme',
  },

  kpis: {
    title: 'Platform performance indicators',
    refresh: 'Refresh the indicators',
    refreshed: 'The indicators were refreshed',
    error: 'The indicators could not be loaded. Please try again later.',
    retry: 'Try again',
    slow: 'Still loading the indicators; this can take a few seconds',

    groups: {
      participation: 'Participation and reach',
      completion: 'Content completion',
      knowledge: 'Knowledge and exams',
      compliance: 'Compliance and policies',
    },

    headline: {
      title: 'Platform performance indicators',
      activeRate: 'Active user rate',
      activeRateUnit: 'targeted employees',
      activeRateHint: 'The share of targeted employees who signed in during the selected window.',
      pathCompletionRate: 'Path completion rate',
      pathCompletionUnit: 'assigned paths',
      pathCompletionHint: 'The share of assigned paths employees have completed, across all periods.',
      averageExamScore: 'Average exam score',
      averageScoreUnit: 'exam attempts',
      averageScoreHint: 'The average score of counted exam attempts, across all periods.',
      complianceRate: 'Policy acknowledgement rate',
      complianceUnit: 'targeted acknowledgements',
      complianceHint: 'The share of targeted acknowledgements that are complete, across all periods.',
    },

    scope: {
      window: 'Activity window',
      lastFew: 'Last',
      daysFew: 'days',
      daysMany: 'days',
      windowHint:
        'This window only decides who counts as an active user. Content completion, exams and policy acknowledgement are counted across all periods.',
    },

    totalTargeted: 'Total targeted employees',
    registered: 'Registered employees',
    activeEmployees: 'Active employees',
    participatingDepartments: 'Participating departments',

    publishedPaths: 'Published paths',
    employeesStarted: 'Employees who started content',
    employeesCompleted: 'Employees who completed a path',
    employeesPassed: 'Employees who passed a path',

    policyTargets: 'Targeted acknowledgements',
    acknowledgedTargets: 'Acknowledged targets',
    pendingAck: 'Pending acknowledgements',
    feedback: 'Employees who gave feedback',

    funnel: {
      title: 'The participation funnel',
      scope: 'All periods',
      targeted: 'Targeted',
      started: 'Started content',
      completed: 'Completed a path',
      passed: 'Passed a path',
    },

    departments: {
      title: 'Most engaged departments',
      scope: 'Highest to lowest',

      partial: 'Only the most and least engaged',
      seriesActive: 'Active',
      seriesInactive: 'Inactive',
      emptyTitle: 'No departments to show',
    },

    paths: {
      title: 'Paths by completion rate',
      scope: 'Highest to lowest',
      seriesCompleted: 'Completed',
      seriesRemaining: 'Not completed',
      emptyTitle: 'No paths to show',
    },

    complianceByDept: {
      title: 'Policy acknowledgement by department',
      scope: 'Lowest to highest',
      seriesAcknowledged: 'Acknowledged',
      seriesPending: 'Not acknowledged',
      emptyTitle: 'No acknowledgement data to show',
    },

    empty: { desc: 'There is not enough data to show this indicator.' },
  },

  /** The statistics on their own page — the fullscreen board and the printed report. */
  report: {
    title: 'General statistics',
    period: 'Period',
    allTime: 'All time',
    generatedAt: 'Report date',
    generatedBy: 'Prepared by',
    fullscreen: 'Full screen',
    exitFullscreen: 'Exit full screen',
    print: 'Save as PDF',
    failed: 'The statistics could not be loaded',
    footer: 'Rawam — Security Awareness Platform',
    outcomesNote: 'Of programme assignments: someone assigned two programmes counts twice.',
    sections: {
      platform: 'Platform content',
      licence: 'Licence',
      outcomes: 'Trainee outcomes',
      gaps: 'Follow-up indicators',
    },
    /** The exported file — a document, with a cover, not a photograph of the board. */
    document: {
      eyebrow: 'Periodic report',
      subtitle: 'A full account of activity on the security awareness platform: training content, trainee outcomes, performance indicators and policy compliance.',
      summary: 'Executive summary',
      summaryNote: 'Four headline indicators summarising the state of the platform; the detail follows.',
      content: 'Content available on the platform',
      seatsUsed: 'Accounts in use',
      daysUsed: 'Licence period consumed',
      remaining: 'remaining',
      confidential: 'Internal document',
    },
    progress: {
      title: 'Preparing the report',
      note: 'Collecting the statistics, drawing the charts and building the pages. Please wait and keep this page open.',
      steps: {
        data: 'Reading the statistics…',
        charts: 'Drawing the charts…',
        render: 'Laying out the pages…',
        compose: 'Building the PDF…',
        save: 'Saving the file…',
      },
    },
    preparing: 'Preparing the report…',
    prepared: 'Report downloaded',
    exportFailed: 'The report could not be prepared',
    licence: { type: 'Licence type', seats: 'Total accounts' },
    gaps: {
      notWatching: 'Users who have not watched the content',
      notUpdatedProfile: 'Users who have not updated their profile',
    },
    table: {
      start: 'Start date',
      end: 'End date',
      groups: 'Departments',
      users: 'Users',
      empty: 'No training programmes',
    },
  },

  fullScreen: 'Open full screen',

  exportExcel: 'Export to Excel',
  exportPdf: 'Export to PDF',
  noLicenceData: '—',
  overview: {
    title: 'Overview',
    hello: 'Hello, ',
    people: { one: 'employee', other: 'employees' },
    export: 'Export report',
    cards: 'Awareness programme status',
    openReport: 'Open the report',
    openList: 'Open the list',
    training: {
      name: 'Training completion',
      info: 'The share of programme assignments completed by the end of the period, passed or not.',
      basis: 'Completed assignments',
      follow: 'Overdue',
    },
    knowledge: {
      name: 'Assessment pass rate',
      info: 'The share of assessment participants who passed in the period.',
      basis: 'Passed',
      follow: 'Did not pass',
    },
    clicks: {
      name: 'Click rate',
      info: 'The share of simulated phishing emails whose link was clicked in the period. Lower is better.',
      basis: 'Emails clicked',
      follow: 'Clicked',
    },
    reporting: {
      name: 'Report rate',
      info: 'The share of simulated phishing emails their recipients reported in the period. Higher is better.',
      basis: 'Emails reported',
      campaigns: 'Campaigns',
    },
    policies: {
      name: 'Policy acknowledgement',
      info: 'The share of policy assignments acknowledged by the end of the period.',
      basis: 'Acknowledged assignments',
      follow: 'Awaiting acknowledgement',
    },
    programmes: {
      title: 'Training programme performance',
      info: 'Where each programme\'s assignees stand at the end of the period. The share is those who completed it out of those assigned.',
      assigned: 'assigned',
      passed: 'Passed',
      failed: 'Did not pass',
      inProgress: 'In progress',
      notStarted: 'Not started',
      showAll: 'Show all programmes',
      showLess: 'Show fewer',
      empty: 'No programme has anyone assigned in this period',
    },

    inventory: {
      title: 'Platform content',
      info: 'Everything the platform holds now, and how much of it is active or open today. Choose any item to open its page.',
      items: {
        programs: { label: { one: 'training programme', other: 'training programmes' }, live: 'active' },
        courses: { label: { one: 'training course', other: 'training courses' }, live: 'available' },
        content: { label: { one: 'learning content item', other: 'learning content items' }, parts: { one: 'lesson', other: 'lessons' } },
        questions: { label: { one: 'question', other: 'questions' } },
        assessments: { label: { one: 'assessment', other: 'assessments' }, live: 'open' },
        surveys: { label: { one: 'survey', other: 'surveys' }, live: 'open' },
        policies: { label: { one: 'policy', other: 'policies' }, live: 'enabled' },
        bulletins: { label: { one: 'awareness bulletin', other: 'awareness bulletins' }, live: 'published' },
        phishingTemplates: { label: { one: 'phishing template', other: 'phishing templates' }, live: 'enabled' },
        phishingCampaigns: { label: { one: 'phishing campaign', other: 'phishing campaigns' }, live: 'running' },
        badges: { label: { one: 'badge', other: 'badges' }, live: 'enabled' },
        certificates: { label: { one: 'certificate template', other: 'certificate templates' } },
      },
      failed: 'The platform content could not be loaded',
    },
    activity: {
      title: 'Course exam completions per day',
      info: 'How many course exams employees completed on each day of the period, passed and not passed, in the courses of the programmes covered. Assessments are not included.',
      passed: 'Passed',
      failed: 'Failed',
      empty: 'Nobody completed a course exam in this period',
    },
    assessments: {
      title: 'Assessment results',
      info: 'Each assessment\'s results since it began, not limited by the chosen period. The share is those who passed out of those who took part.',
      passed: 'Passed',
      failed: 'Did not pass',
      noResults: 'No results',
      empty: 'No assessment has run yet',
    },
    licence: {
      title: 'License status',
      details: 'License details',
    },
  },
}
